import Axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { CurrencyPayItem } from './redux/models/currencyPay';
import useUpdatePaymentStatus from './CurrencyPay/useUpdatePaymentStatus';
import { iRootState, Dispatch } from './redux/store';

interface PaymentResult {
  data: {
    id: string;
    status_code: number;
    payment_method: string;
  };
}

const useLaunchCurrencyPayWindow = function useLaunchCurrencyPayWindow(): {
  launchCurrencyPayWindow: (
    total: number,
    items: CurrencyPayItem[],
    orderId: string | null,
    orderDesc: string | null,
    tax: number | null,
    shipping: number | null,
    useFortis: boolean | null,
    hasACH: boolean | null,
    hasСС: boolean | null,
    dealerId: string | null
  ) => void;
} {
  const { updatePaymentStatus } = useUpdatePaymentStatus();
  const dispatch = useDispatch<Dispatch>();

  const selectedTerms = useSelector((state: iRootState) =>
    state.paymentTerms.terms.filter(t => t.checked)
  );

  const launchCurrencyPayWindow = async (
    total: number,
    items: CurrencyPayItem[],
    orderId: string | null,
    orderDesc: string | null,
    tax: number | null,
    shipping: number | null,
    useFortis: boolean | null,
    hasACH: boolean | null,
    hasСС: boolean | null,
    dealerId: string | null
  ) => {
    if (useFortis) {
      const axiosApiInstance = Axios.create({
        baseURL: window.AutoQuotes.config.apiBaseUrl
      });
      try {
        const { data: transactionData } = await axiosApiInstance.get(
          `fortis/transactionIntention/${dealerId}/${total}/${hasACH}/${hasСС}`
        );

        const elements = new window.Commerce.elements(transactionData.data.client_token); // eslint-disable-line
        elements.create({
          container: '#payment',
          theme: 'default',
          showSubmitButton: true,
          environment: window.AutoQuotes.config.fortisEnvironment,
          fields: {
            additional: [{ name: 'email_address', required: true }],
            billing: [
              { name: 'address', required: true, value: '123 Street' },
              { name: 'country', required: true, value: 'US' },
              { name: 'state', required: true, value: 'MI' },
              { name: 'city', required: false },
              { name: 'postal_code', required: true }
            ]
          }
        });

        elements.on('done', async (result: PaymentResult) => {
          let paymentStatus = '';
          let paymentStatusCode = 1;
          const transactionId = result.data.id;
          const paymentMethod = result.data.payment_method;

          if (paymentMethod === 'cc') {
            if (result.data.status_code === 101) {
              paymentStatus = 'Paid';
              paymentStatusCode = 1;
            } else if (
              [102, 111, 121, 131, 132, 133, 134, 191, 301, 331].includes(
                result.data.status_code
              )
            ) {
              paymentStatus = 'Payment Unsuccessful';
            }
          } else if (paymentMethod === 'ach') {
            paymentStatusCode = 10;
            if (result.data.status_code === 134) {
              paymentStatus = 'Paid';
            } else if ([131, 132, 133].includes(result.data.status_code)) {
              paymentStatus = 'Pending';
            } else {
              paymentStatus = 'Payment Unsuccessful';
            }
          }

          await updatePaymentStatus(
            paymentStatus,
            transactionId,
            paymentStatusCode,
            selectedTerms.map(t => t.termOrder)
          );
        });

        const paymentForm: any = document.getElementById('paymentForm'); // eslint-disable-line
        paymentForm.showModal();

        document
          .getElementById('fortisPayNowBt')
          ?.addEventListener('click', elements.submit());

        paymentForm.addEventListener('click', (event: any) => { // eslint-disable-line
          if (event.target === paymentForm) {
            paymentForm.close();
          }
        });
      } catch (error: any) { // eslint-disable-line            
        if (error.response) {
          if (error.response.status === 401) {
            dispatch.error.setError({
              title: 'Payment Failed',
              message: 'Invalid Fortis credentials.'
            });
          } else {
            dispatch.error.setError({
              title: 'Payment Failed',
              message: 'An unexpected error occurred. Please try again later.'
            });
          }
        } else {
          // Something happened in setting up the request that triggered an Error
          console.log('Error message:', error.message); // eslint-disable-line      
        }
      }
    } else {
      window.CurrencyPayWindow.open({
        total,
        items,
        orderId,
        orderDesc,
        tax,
        shipping
      });
    }
  };

  return { launchCurrencyPayWindow };
};

export default useLaunchCurrencyPayWindow;
